import React from 'react';
import ReactDOM from 'react-dom';
import { APP_OVERLAY_Z_INDEX } from '../../styles/layers';
import styled from 'styled-components';
import { ButtonGroup, TextButton, PrimaryButton } from '../../styles/CommonStyles';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'full';
  maxWidth?: string;
  zIndex?: number;
  closeOnOverlayClick?: boolean;
  noScroll?: boolean;
}

const ModalOverlay = styled.div<{ isOpen: boolean; zIndex: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  /* Top padding ensures the modal never overlaps the header; bottom clears prompt bar */
  padding: max(10vh, 88px) 1rem max(12vh, 120px) 1rem;
  z-index: ${props => props.zIndex};
  backdrop-filter: blur(3px);
  /* Use flex-start so margin: auto can center the child without symmetric clipping */
  align-items: flex-start;
  justify-content: center;

  @media (max-width: 768px) {
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 160px);
  }
`;

const ModalContainer = styled.div<{ size: string; customMaxWidth?: string; noScroll?: boolean }>`
  background: white;
  border-radius: 12px;
  /* Anchor for the corner close button (task #56). */
  position: relative;
  width: 100%;
  max-width: ${props =>
    props.customMaxWidth ? props.customMaxWidth :
    props.size === 'small' ? '400px' :
    props.size === 'large' ? '800px' :
    props.size === 'xlarge' ? '1200px' :
    props.size === 'full' ? '95vw' :
    '600px'};
  /* margin: auto pushes short modals to the center; max-height: 100% bounds tall ones within overlay padding */
  margin: auto;
  max-height: 100%;
  overflow-y: ${props => props.noScroll ? 'hidden' : 'auto'};
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-weight: 600;
  color: #333;
  font-size: 1.25rem;
`;

const ModalDescription = styled.p`
  margin: 0.75rem 0 0;
  color: #666;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const ModalContent = styled.div<{ noPadding?: boolean; flexGrow?: boolean }>`
  padding: ${props => props.noPadding ? '0' : '1.5rem'};
  flex-grow: ${props => props.flexGrow ? '1' : '0'};
  overflow-y: ${props => props.flexGrow ? 'hidden' : 'visible'};
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  padding: 0 1.5rem 1.5rem;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const DefaultFooter: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <ButtonGroup>
    <TextButton onClick={onClose}>Cancel</TextButton>
    <PrimaryButton onClick={onClose}>Confirm</PrimaryButton>
  </ButtonGroup>
);

/**
 * Corner close (task #56, owner report: users get stuck in popups) — every
 * dismissible modal shows an unmistakable ×, and ESC works too. Both follow
 * the SAME dismissibility flag as the overlay click, so a flow that
 * deliberately makes a modal non-dismissible keeps all three exits off.
 */
const CornerClose = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid #E6E4DE;
  background: #fff;
  color: #857F73;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: #F6F4EF;
    color: #333;
  }
`;

/** Open (dismissible) modals in mount order — ESC targets the last entry. */
const openModalEscStack: symbol[] = [];

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'medium',
  closeOnOverlayClick = true,
  maxWidth,
  zIndex = APP_OVERLAY_Z_INDEX,
  noScroll = false
}) => {
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  // ESC closes a dismissible open modal (task #56) — only the TOPMOST one
  // when modals stack (e.g. a confirm dialog above a settings modal), so one
  // keypress never collapses a whole stack. Listener only exists while open.
  const escTokenRef = React.useRef<symbol>();
  if (!escTokenRef.current) escTokenRef.current = Symbol('modal');
  React.useEffect(() => {
    if (!isOpen || !closeOnOverlayClick) return;
    const token = escTokenRef.current as symbol;
    openModalEscStack.push(token);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openModalEscStack[openModalEscStack.length - 1] !== token) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = openModalEscStack.indexOf(token);
      if (i >= 0) openModalEscStack.splice(i, 1);
    };
  }, [isOpen, closeOnOverlayClick, onClose]);

  if (typeof document === 'undefined') return null;

  const modalContent = (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick} zIndex={zIndex} data-prest0n-portal="true">
      <ModalContainer size={size} customMaxWidth={maxWidth} noScroll={noScroll} onClick={e => e.stopPropagation()}>
        {closeOnOverlayClick && (
          <CornerClose type="button" aria-label="close" title="close" onClick={onClose}>
            ✕
          </CornerClose>
        )}
        {(title || description) && (
          <ModalHeader>
            {title && <ModalTitle>{title}</ModalTitle>}
            {description && <ModalDescription>{description}</ModalDescription>}
          </ModalHeader>
        )}
        
        <ModalContent noPadding={noScroll} flexGrow={noScroll}>{children}</ModalContent>
        
        {footer !== undefined ? (
          <ModalFooter>{footer}</ModalFooter>
        ) : (
          <ModalFooter>
            <DefaultFooter onClose={onClose} />
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default Modal; 